import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import supabase from "../config/supabaseConfig";
import { matriculasService, REGRAS_AVAL } from "../services/matriculas.service";
import "../styles/CadastroAlunoNovo.css";

// Opções (tier = campo `plano` salvo hoje). REGRAS_AVAL agora mora no service
// matriculas.service (fonte única) — aqui só a importamos pra montar o preview.
const TIERS = ["8 Check-in", "12 Check-in", "16 Check-in", "Premium", "Black"];
const DURACOES = ["Mensal", "Semestral", "Anual"];
const NIVEIS = ["Iniciante", "Intermediário", "Avançado"];

// Duração do form → formato do banco (lowercase sem acento).
const formatoDe = (duracao) =>
  duracao
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const calcularIdade = (dataNascimento) => {
  if (!dataNascimento) return null;
  const hoje = new Date();
  const nasc = new Date(dataNascimento);
  let idade = hoje.getFullYear() - nasc.getFullYear();
  const m = hoje.getMonth() - nasc.getMonth();
  if (m < 0 || (m === 0 && hoje.getDate() < nasc.getDate())) idade--;
  return idade;
};

const CadastroAlunoNovo = () => {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    nome: "",
    telefone: "",
    dataNascimento: "",
    nivel: "Intermediário",
    lesao: "Não",
    tipo_lesao: "",
    objetivo: "",
    observacoes: "",
    exercicios_obrigatorios: "",
    status: "ativo",
  });
  const [tier, setTier] = useState("12 Check-in");
  const [duracao, setDuracao] = useState("Semestral"); // preview Fase 3
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState(null);

  const set = (campo, valor) => setForm((f) => ({ ...f, [campo]: valor }));

  // Agenda de avaliações reativa (mensal não passa por avaliação).
  const avaliacoes = duracao === "Mensal" ? [] : REGRAS_AVAL[tier] || [];

  const salvar = async () => {
    if (!form.nome.trim()) {
      setErro("Informe o nome do aluno.");
      return;
    }
    try {
      setSalvando(true);
      setErro(null);
      const lesaoCorrigida = form.lesao === "Não" ? "Nao" : form.lesao;
      const alunoParaSalvar = {
        nome: form.nome.trim(),
        telefone: form.telefone || null,
        data_nascimento: form.dataNascimento || null,
        idade: calcularIdade(form.dataNascimento),
        status: form.status || "ativo",
        lesao: lesaoCorrigida,
        tipo_lesao: lesaoCorrigida !== "Nao" ? form.tipo_lesao || null : null,
        objetivo: form.objetivo || null,
        plano: tier,
        nivel: form.nivel || "Iniciante",
        observacoes: form.observacoes || null,
        exercicios_obrigatorios: form.exercicios_obrigatorios || null,
        // Dual-write: `alunos.plano` continua gravado na transição. A duração e a
        // agenda de avaliações passam a persistir em matriculas/avaliacoes abaixo.
      };
      // 1) Cria o aluno e recupera o id gerado.
      const { data: alunoCriado, error } = await supabase
        .from("alunos")
        .insert([alunoParaSalvar])
        .select("id")
        .single();
      if (error) throw error;

      // 2) Cria a matrícula. Se falhar aqui, o aluno JÁ foi salvo — não
      // perdemos o cadastro; avisamos que só o contrato falhou.
      //
      // NÃO gera agenda de avaliações (27/07): o modelo passou a ser rolante,
      // ancorado em `data_ultima_avaliacao`. Aluno novo nasce sem avaliação
      // realizada, então entra na lista de "nunca avaliado" — quando fizerem a
      // avaliação de entrada e registrarem a data na edição, a próxima é
      // agendada sozinha. Antes daqui saía uma agenda inteira automática, que
      // é o que gerava avaliação fantasma já atrasada.
      try {
        await matriculasService.criarMatricula({
          alunoId: alunoCriado.id,
          tierLegado: tier,
          formato: formatoDe(duracao),
          dataInicio: new Date().toISOString().slice(0, 10),
        });
      } catch (errMat) {
        console.error("Erro ao criar matrícula/agenda do aluno:", errMat);
        setErro(
          "Aluno salvo, mas houve erro ao registrar o contrato (matrícula/avaliações). " +
            "Edite o aluno e salve novamente para completar. Detalhe: " +
            errMat.message
        );
        setSalvando(false);
        return;
      }

      navigate("/alunos");
    } catch (err) {
      console.error("Erro ao cadastrar aluno:", err);
      setErro("Erro ao cadastrar aluno: " + err.message);
    } finally {
      setSalvando(false);
    }
  };

  const Pills = ({ opcoes, valor, onChange }) => (
    <div className="ca-pillset">
      {opcoes.map((op) => (
        <button
          type="button"
          key={op}
          className={`ca-pill ${op === valor ? "active" : ""} ${
            op === "Black" ? "dark" : ""
          }`}
          onClick={() => onChange(op)}
        >
          {op}
        </button>
      ))}
    </div>
  );

  return (
    <div className="cadastro-aluno-novo">
      <div className="ca-header">
        <h1>Cadastro de Aluno</h1>
        <span className="ca-sub">Novo aluno</span>
      </div>

      {erro && <div className="ca-erro">{erro}</div>}

      <div className="ca-grid">
        {/* Coluna do formulário */}
        <div className="ca-card">
          <div className="ca-section-title">Dados básicos</div>
          <div className="ca-two">
            <label className="ca-field">
              <span>Nome completo*</span>
              <input
                type="text"
                value={form.nome}
                onChange={(e) => set("nome", e.target.value)}
                placeholder="Nome do aluno"
              />
            </label>
            <label className="ca-field">
              <span>Telefone (WhatsApp)</span>
              <input
                type="tel"
                value={form.telefone}
                onChange={(e) => set("telefone", e.target.value)}
                placeholder="(24) 9____-____"
              />
            </label>
          </div>
          <div className="ca-two">
            <label className="ca-field">
              <span>Data de nascimento</span>
              <input
                type="date"
                value={form.dataNascimento}
                onChange={(e) => set("dataNascimento", e.target.value)}
              />
            </label>
            <label className="ca-field">
              <span>Status</span>
              <select
                value={form.status}
                onChange={(e) => set("status", e.target.value)}
              >
                <option value="ativo">Ativo</option>
                <option value="inativo">Inativo</option>
              </select>
            </label>
          </div>

          <hr className="ca-hr" />
          <div className="ca-section-title">Plano — tier</div>
          <p className="ca-note">Define o número de avaliações.</p>
          <Pills opcoes={TIERS} valor={tier} onChange={setTier} />

          <div className="ca-section-title ca-mt">Plano — duração</div>
          <p className="ca-note">
            Define o intervalo do contrato. Mensal não passa por avaliação.
          </p>
          <Pills opcoes={DURACOES} valor={duracao} onChange={setDuracao} />

          <div className="ca-section-title ca-mt">Nível</div>
          <Pills
            opcoes={NIVEIS}
            valor={form.nivel}
            onChange={(v) => set("nivel", v)}
          />

          <div className="ca-section-title ca-mt">Lesão / restrição</div>
          <select
            className="ca-select"
            value={form.lesao}
            onChange={(e) => set("lesao", e.target.value)}
          >
            <option value="Não">Não</option>
            <option value="Sim - Lesao Moderada">Sim - Lesão Moderada</option>
            <option value="Sim - Lesao Grave">Sim - Lesão Grave</option>
          </select>
          {form.lesao !== "Não" && (
            <label className="ca-field ca-mt">
              <span>Descrição da lesão</span>
              <textarea
                rows={2}
                value={form.tipo_lesao}
                onChange={(e) => set("tipo_lesao", e.target.value)}
                placeholder="Ex.: ombro direito, em avaliação"
              />
            </label>
          )}

          <label className="ca-field ca-mt">
            <span>Objetivo de treino</span>
            <textarea
              rows={2}
              value={form.objetivo}
              onChange={(e) => set("objetivo", e.target.value)}
              placeholder="Ex.: hipertrofia, condicionamento…"
            />
          </label>
          {/* Feedback Tales 08/07: "Observações" → "Treino" (coluna
              alunos.observacoes) + campo novo "Exercícios obrigatórios",
              espelhados na full screen do aluno. */}
          <label className="ca-field ca-mt">
            <span>Treino</span>
            <textarea
              rows={4}
              value={form.observacoes}
              onChange={(e) => set("observacoes", e.target.value)}
              placeholder="Treino do aluno — use ENTER para separar parágrafos"
            />
          </label>
          <label className="ca-field ca-mt">
            <span>Exercícios obrigatórios</span>
            <textarea
              rows={3}
              value={form.exercicios_obrigatorios}
              onChange={(e) => set("exercicios_obrigatorios", e.target.value)}
              placeholder="Ex.: agachamento livre, prancha 3×40s…"
            />
          </label>

          <div className="ca-actions">
            <button
              type="button"
              className="ca-btn-ghost"
              onClick={() => navigate("/alunos")}
              disabled={salvando}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="ca-btn-primary"
              onClick={salvar}
              disabled={salvando}
            >
              {salvando ? "Salvando…" : "Salvar cadastro"}
            </button>
          </div>
        </div>

        {/* Coluna lateral — agenda de avaliações (preview) + regras */}
        <div>
          <div className="ca-card ca-card-dark">
            <div className="ca-overline">Agenda de avaliações</div>
            <div className="ca-plano-sel">
              {tier} · {duracao}
            </div>
            {avaliacoes.length > 0 ? (
              avaliacoes.map((a) => (
                <div key={a.n} className="ca-aval-item">
                  <div className="ca-aval-num">{a.n}</div>
                  <div className="ca-aval-when">
                    {a.q}
                    <small>{a.w}</small>
                  </div>
                </div>
              ))
            ) : (
              <p className="ca-note">
                Plano mensal não passa por avaliação automática. A avaliação é
                feita à parte.
              </p>
            )}
          </div>

          <div className="ca-card ca-mt">
            <div className="ca-section-title">Regras (referência)</div>
            <div className="ca-regras">
              8 Check-in → 1 (entrada)
              <br />
              12 Check-in → 2 (entrada + 4 meses)
              <br />
              16 Check-in → 3 (a cada 3 meses)
              <br />
              Premium → 4 (entrada + 3× a cada 3 meses)
              <br />
              Black → 4 (entrada + 3× a cada 3 meses)
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CadastroAlunoNovo;
